import * as React from 'react';
import {
	Link,
	useLocation,
	useParams,
} from "react-router-dom"
import {
  Drawer,
  AppBar,
  CssBaseline,
  Box,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
} from "@mui/material"
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone';
import UploadFileTwoToneIcon from '@mui/icons-material/UploadFileTwoTone';
import AutoAwesomeMotionTwoToneIcon from '@mui/icons-material/AutoAwesomeMotionTwoTone';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import StorageTwoToneIcon from '@mui/icons-material/StorageTwoTone';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import CurrentClientContext from '../../providers/CurrentClientProvider';
import CopyAllOutlinedIcon from '@mui/icons-material/CopyAllOutlined';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import _ from 'underscore'
import {LibraryBooks, SupportAgent} from "@mui/icons-material";
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import UserService from "../../services/UserService";
import {useRef, useState} from "react";
import Auth from '../../providers/AuthProvider'
import ClientService from "../../services/ClientService";
import TipContext from "../../providers/GlobalTip";
import ClientFeatureFlagService from '../../services/ClientFeatureFlagService'
import constants from '../../constants/constant'
import { hasPermissionBypassAccess } from './PermissionsMiddleware'
import { buildClientPlanPath } from '../../services/ClientDashboardV3'

function buildLegacyNavStyles({ topOffset = 50 }) {
	return {
		nav: {
			backgroundColor: '#422e52',
			color: 'white',
			boxSizing: 'border-box',
			mt: `${topOffset}px`,
			overflow:'visible',
			borderRadius: 0,
			'::-webkit-scrollbar': {
				width: 0,
			}
		},
		navItem: {
			flexDirection: 'column',
			alignItems: 'center',
				color: 'rgba(255,255,255,0.6)',
			transition: 'all 0.3s',
			fontSize: 12,
			p: 0,
				borderBottom: '1px solid rgba(255,255,255,0.1)',
				py: 1,
			':hover': {
					color: 'rgba(255,255,255,1)',
			},
			'& .active': {
					color: 'rgba(255,255,255,1)',
			},
		},
		navItemSection: {
			position: 'relative',
			'.subNav': {
				display:'none',
				position: 'absolute',
				left:79,
				top: 0,
					width: '200px',
					backgroundColor: '#422e52',
				fontSize: 12,
				'.subItem': {
						borderBottom: '1px solid rgba(255,255,255,0.1)',
				},
				'.subItem:last-child': {
					borderBottom: 'none'
				}
			},
			":hover": {
				'.subNav': {
					display:'block',
				}
			},
		},
		navItemIcon: {
			fontSize: '26px',
			mb: 0.5,
		},
	}
}

// Component to hold report library link button for easy re-use
function ReportLibraryLink( { currentClient, styles } ) {
	return (
		<ListItem disableGutters disablePadding sx={styles.navItemSection}>
			<ListItemButton component={Link} sx={styles.navItem} to={`/clients/${currentClient.client.ClientCode}/reports-lib/${currentClient.client.Id}`}>
				<LibraryBooksIcon sx={styles.navItemIcon}/>
				Report Library
			</ListItemButton>
		</ListItem>
	)
}

function ClientDashboardLink({ currentClient, styles }) {
	return (
		<ListItem disableGutters disablePadding sx={styles.navItemSection}>
			<ListItemButton component={Link} sx={styles.navItem} to={`/client/${currentClient.client.ClientCode}/simple-dashboard`}>
				<AssessmentIcon sx={styles.navItemIcon}/>
				Dashboard
			</ListItemButton>
		</ListItem>
	)
}

const retailOrbitSupportFormUrl = 'https://www.cognitoforms.com/ManagementOne1/SERVICEREQUEST';

function getLegacyRoleTags(user) {
	return Array.isArray(user?.roleTags)
		? user.roleTags.map((tag) => String(tag))
		: []
}

function getLegacyNavigationAccess(auth) {
	const roleTags = getLegacyRoleTags(auth?.user)
	const isPlanner = roleTags.includes(String(constants.ROLE_ID.planner))
	const hasAdminBypass = hasPermissionBypassAccess(auth)
	const hasPlanningPermission = Boolean(auth?.checkPermission?.(['full-planning', 'dev-tools']))
	const hasClientInfoPermission = Boolean(auth?.checkPermission?.('view-client-info'))
	const hasAddClientPermission = Boolean(auth?.checkPermission?.('add-client'))

	return {
		canUsePlanningTools: Boolean(isPlanner || hasAdminBypass || hasPlanningPermission),
		canUseAdminTools: Boolean(hasAdminBypass || auth?.checkPermission?.('dev-tools')),
		canAddClient: Boolean(hasAdminBypass || hasAddClientPermission),
		canViewClientInfo: Boolean(isPlanner || hasAdminBypass || hasClientInfoPermission),
		canSeeAnalytics: Boolean(hasAdminBypass || auth?.checkPermission?.('dev-tools')),
		canSeeMasterClassTools: Boolean(hasAdminBypass || auth?.checkPermission?.('dev-tools')),
	}
}

function NavContent({ styles = buildLegacyNavStyles({}) }) {
	const currentClient = CurrentClientContext.useClient()
	const auth = Auth.useAuth()
	const tipContext = TipContext.useMessage()
	const navigationAccess = getLegacyNavigationAccess(auth)
	const livePlanHref = buildClientPlanPath({
		clientCode: currentClient?.client?.ClientCode,
		client: currentClient?.client,
		preferredMode: 'legacy',
	})

	React.useEffect(() => {
	}, [])

	const getZendLink = () => {
		UserService.getZendUrl().then(res => {
			if(res.url) {
				window.open(res.url, '_blank', 'location=no')
			}
		})
	}

	const getSupportFormLink = () => {
		let params = {}
		if (auth.user) {
			params.Name = {First:auth.user.first_name, Last: auth.user.last_name }
			params.EmailAddress = auth.user.email
		}
		params.ClientCode = currentClient.client.ClientCode
		params.StoreName = currentClient.client.Name

		//encode onto url under "entry"
		let url = retailOrbitSupportFormUrl + '?entry=' + encodeURIComponent(JSON.stringify(params))

		window.open(url)
	}

	const clearCache = async (action) => {
		if (confirm('This action purge all cache for client. Are you sure?') === true){
			if (action === 'regenerate') {
				await ClientService.regenerateCache(currentClient.client.Id)
				tipContext.showSuccess("Cache regenerating")
			} else {
				await ClientService.clearCache(currentClient.client.Id)
				tipContext.showSuccess("Cache cleared")
			}
		}
	}

	return <>
		<List disablePadding >
			<ListItem disableGutters disablePadding >
				<ListItemButton component={Link} sx={styles.navItem} to='/'>
					<HomeTwoToneIcon sx={styles.navItemIcon}/>
					Home
				</ListItemButton>
			</ListItem>
			{/* TODO: Add role check as opposed to checkPermission. Currently using "role-create" since that would mean admin, but this should really be checking is Admin/Support */}
			{navigationAccess.canUsePlanningTools ?
				<ListItem disableGutters disablePadding sx={styles.navItemSection}>
					<ListItemButton component={Link} sx={styles.navItem} to={`/plan/copy`}>
						<CopyAllOutlinedIcon sx={styles.navItemIcon}/>
						Copy Plan
					</ListItemButton>
				</ListItem>
			: null }
			{navigationAccess.canAddClient ?
				<ListItem disableGutters disablePadding sx={styles.navItemSection}>
					<ListItemButton component={Link} sx={styles.navItem} to={`/client/add`}>
						<LocalHospitalIcon sx={styles.navItemIcon}/>
						Add Client
					</ListItemButton>
				</ListItem>
			: null }
			{navigationAccess.canUseAdminTools ?
				<ListItem disableGutters disablePadding sx={styles.navItemSection}>
					<ListItemButton component={Link} sx={styles.navItem} to={`/`} onClick={(e) => e.preventDefault()}>
						<AutoAwesomeMotionTwoToneIcon sx={styles.navItemIcon}/>
						Admin Tools
					</ListItemButton>
					<Box className="subNav">
						<ListItemButton component={Link} to={`/admin/custom-calendar-management`} className="subItem">Custom Calendars</ListItemButton>
						<ListItemButton component={Link} to={`/admin/orbit-ai/forecast-runs`} className="subItem">AI Admin</ListItemButton>
						<ListItemButton component={Link} to={`/admin/orbit-metadata`} className="subItem">Orbit AI Metadata</ListItemButton>

						<ListItemButton component={Link} to={`/admin/content-review`} className="subItem">Curated Content</ListItemButton>
						{Boolean(hasPermissionBypassAccess(auth) || auth.checkPermission('dev-tools')) ?
							<ListItemButton component={Link} to={`/dev-tools`} className="subItem">
								Dev Tools
							</ListItemButton>
						: null}
						<ListItemButton component={Link} to={`/admin/flightcontrol`} className="subItem">Flight Control</ListItemButton>
						{/* Legacy Reports (original library) - only show when a client is selected */}
						{!_.isEmpty(currentClient.client) && (
							<ListItemButton
								component={Link}
								to={`/clients/${currentClient.client.ClientCode}/report-library/${currentClient.client.Id}`}
								className="subItem"
							>
								Legacy Reports
							</ListItemButton>
						)}
					</Box>
				</ListItem>
			: null}
			{!_.isEmpty(currentClient.client) && 
				<>
					<ClientDashboardLink currentClient={currentClient} styles={styles} />
					{/* <ListItem disableGutters disablePadding sx={cssInJs.navItemSection}>
						<ListItemButton component={Link} sx={cssInJs.navItem} to={`/clients/${currentClient.client.ClientCode}/plan/locationclasses/default`}>
							<TimelineOutlinedIcon sx={cssInJs.navItemIcon}/>
							Plan
						</ListItemButton>
					</ListItem> */}
					{/* KPI moved to Reports Library - commented out for quick fallback if needed */}
					{/* <ListItem disableGutters disablePadding sx={cssInJs.navItemSection}>
						<ListItemButton className={'menu-item'} component={Link} sx={cssInJs.navItem} to={`/clients/${currentClient.client.ClientCode}/kpi/${currentClient.client.Id}`}>
							<AssessmentIcon sx={cssInJs.navItemIcon}/>
							KPI
						</ListItemButton>
					</ListItem> */}
					{navigationAccess.canSeeAnalytics ?
						<ListItem disableGutters disablePadding sx={styles.navItemSection}>
						<ListItemButton component={Link} sx={styles.navItem} to={`/clients/${currentClient.client.ClientCode}/analytics/${currentClient.client.Id}`}>
							<AssessmentIcon sx={styles.navItemIcon}/>
							Analytics
						</ListItemButton>
					</ListItem>
						: null}
					{/* Conditionally lock out non admin users based on env variable */}
					{process.env.REACT_APP_REPORT_LOCKOUT_CURRENT_MONTH ?
						<>
							{navigationAccess.canSeeAnalytics ?
																					<ReportLibraryLink currentClient={currentClient} styles={styles} />
							: null}
						</>
															: 	<ReportLibraryLink currentClient={currentClient} styles={styles} /> }

					{ClientFeatureFlagService.isClientFeatureFlagEnabled(currentClient.client) ?
						<ListItem disableGutters disablePadding sx={styles.navItemSection}>
							<ListItemButton component={Link} sx={styles.navItem} to={`/clients/${currentClient.client.ClientCode}/indie-ai`}>
								<AutoAwesomeMotionTwoToneIcon sx={styles.navItemIcon}/>
								Indie AI
							</ListItemButton>
						</ListItem>
					: null}

					<ListItem disableGutters disablePadding sx={styles.navItemSection}>
						<ListItemButton component={Link} sx={styles.navItem} to={`/clients/${currentClient.client.ClientCode}/upload-data`}>
							<StorageTwoToneIcon sx={styles.navItemIcon}/>
							Data
						</ListItemButton>
						<Box className="subNav">
							<ListItemButton component={Link} to={`/clients/${currentClient.client.ClientCode}/upload-data`} className="subItem">Upload Data</ListItemButton>
						</Box>
					</ListItem>

					{navigationAccess.canUsePlanningTools ?
						<ListItem disableGutters disablePadding sx={styles.navItemSection}>
							<ListItemButton component={Link} sx={styles.navItem} to={livePlanHref}>
								<TimelineOutlinedIcon sx={styles.navItemIcon}/>
								Plan
							</ListItemButton>
							<Box className="subNav">
								<ListItemButton component={Link} to={`/clients/${currentClient.client.ClientCode}/planninig/${currentClient.client.Id}/preview`} className="subItem">Plan Preview</ListItemButton>
											<ListItemButton component={Link} onClick={() => clearCache('regenerate')} className="subItem">Regenerate Cache</ListItemButton>
											<ListItemButton component={Link} onClick={() => clearCache('clear')} className="subItem">Clear Cache</ListItemButton>
								<ListItemButton component={Link} to={`/clients/${currentClient.client.ClientCode}/plan/other-tools`} className="subItem">Other Plan Tools</ListItemButton>
							</Box>
						</ListItem>
					: null}
					{/* <ListItem disableGutters disablePadding sx={cssInJs.navItemSection}>
					<ListItemButton component={Link} sx={cssInJs.navItem} to={`/clients/${currentClient.client.ClientCode}/planninig/${currentClient.client.Id}/preview`}> <StorageTwoToneIcon sx={cssInJs.navItemIcon}/>Plan Preview</ListItemButton>
					</ListItem> */}
					{/* <ListItem disableGutters disablePadding sx={cssInJs.navItemSection}>
						<ListItemButton component={Link} sx={cssInJs.navItem} to={`/clients/${currentClient.client.ClientCode}/planninig/${currentClient.client.Id}/preview`}>
							<StorageTwoToneIcon sx={cssInJs.navItemIcon}/>
							Planning
						</ListItemButton>
						<Box className="subNav">
							<ListItemButton component={Link} to={`/clients/${currentClient.client.ClientCode}/planninig/${currentClient.client.Id}/preview`} className="subItem">Plan Preview</ListItemButton>
						</Box>
					</ListItem> */}
						<ListItem disableGutters disablePadding sx={styles.navItemSection}>
							<ListItemButton component={Link} sx={styles.navItem} to={`/clients/${currentClient.client.ClientCode}/profile`}>
								<BusinessOutlinedIcon sx={styles.navItemIcon}/>
								Profile
							</ListItemButton>
							{navigationAccess.canViewClientInfo ?
								<Box className="subNav">
									<ListItemButton component={Link} to={`/clients/${currentClient.client.ClientCode}/locations`} className="subItem">Locations</ListItemButton>
									<ListItemButton component={Link} to={`/clients/${currentClient.client.ClientCode}/classes`} className="subItem">Classes</ListItemButton>
									<ListItemButton component={Link} to={`/clients/${currentClient.client.ClientCode}/categories`} className="subItem">Categories</ListItemButton>
									<ListItemButton component={Link} to={`/clients/${currentClient.client.ClientCode}/import-class`} className="subItem">Import Class</ListItemButton>
									<ListItemButton component={Link} to={`/clients/${currentClient.client.ClientCode}/pos-mapping`} className="subItem">POS Mapping</ListItemButton>
									{navigationAccess.canSeeMasterClassTools ?
										<>
											<ListItemButton component={Link} to={`/clients/${currentClient.client.ClientCode}/master-class`} className="subItem">Master Class</ListItemButton>
											<ListItemButton component={Link} to={`/clients/${currentClient.client.ClientCode}/tags`} className="subItem">Tags</ListItemButton>
										</>
									: null}
									{navigationAccess.canAddClient ?
										<ListItemButton component={Link} to={`/client/${currentClient.client.ClientCode}/setup`} className="subItem">Setup</ListItemButton>
									: null}
								</Box>
							: null}
						</ListItem>
					{/* {auth.checkPermission('role-create') ?
						<ListItem disableGutters disablePadding sx={cssInJs.navItemSection}>
							<ListItemButton component={Link} sx={cssInJs.navItem} to={`/clients/${currentClient.client.ClientCode}/sync`}>
								<AutoAwesomeMotionTwoToneIcon sx={cssInJs.navItemIcon}/>
								Sync
							</ListItemButton>
						</ListItem>
					: null} */}
					<ListItem disableGutters disablePadding sx={styles.navItemSection}>
						<ListItemButton component={Box} sx={styles.navItem}>
							<SupportAgent sx={styles.navItemIcon}/>
							Need Help?
						</ListItemButton>
						<Box className="subNav">
							<ListItemButton component={Box} className="subItem" onClick={() => getZendLink()}>24/7 Help Center</ListItemButton>
							<ListItemButton component={Box} className="subItem" onClick={() => getSupportFormLink()}>Submit a Ticket</ListItemButton>
						</Box>
					</ListItem>
				</>
			}
		</List>
	</>
}

function LeftNav(props) {
	const styles = buildLegacyNavStyles({
		topOffset: props.topOffset,
	})

	return (
		<Box component="nav" sx={{ width: { sm: props.width }, flexShrink: { sm: 0 } }}>
			<Drawer
        variant="temporary"
        open={props.navOpen}
        onClose={() => {props.onNavClose()}}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
          	width: props.width,
				   	...styles.nav,
          },
        }}
      >
				  <NavContent styles={styles} />
      </Drawer>
      <Drawer 
        variant="permanent" 
        sx={{
          display: { xs: 'none', sm: 'block' }, 
          '& .MuiDrawer-paper': { 
          	width: props.width,
				   	...styles.nav,
          },
        }}
      >
				  <NavContent styles={styles} />
      </Drawer>
		</Box>
	)
}

export default LeftNav

